/**
 * The zone every TIMESTAMP is displayed in.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THIS IS NOT `tenants.business_timezone`, AND CONFLATING THEM WOULD BE A BUG
 * IN THE BOOKS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `business_timezone` decides WHICH CALENDAR DAY a transaction belongs to. §5.4:
 * a transaction at 7pm on 31 December in America/Chicago is a December
 * transaction, and `toBusinessDate` on the server turns an instant into that
 * date before anything is stored. Change it and you change which period entries
 * fall in — retroactively, for entries already posted and possibly already in a
 * closed year.
 *
 * What this file governs is only how an INSTANT is rendered: "when was this
 * posted", "when did that sync run". Those are `timestamptz` columns, the same
 * moment everywhere, and showing them in the reader's own zone is a display
 * choice with no accounting consequence at all.
 *
 * So the selector on the Settings screen writes HERE and never touches
 * `business_timezone`. A future setting for the business zone is a different
 * control with a different warning on it, and it belongs to the server.
 *
 * WHY `entry_date` IS UNTOUCHED BY ANY OF THIS. It is a DATE, not an instant —
 * a calendar day with no time in it. Feeding one through a zone conversion is
 * how `2026-08-22` becomes `2026-08-21` on a host east of UTC, which is the
 * exact bug `scripts/q.mjs` documents at length. Dates are printed as stored.
 *
 * WHY PER-BROWSER AND NOT PER-TENANT
 *
 * It answers "what time is it where I am", which is a property of the reader.
 * It defaults to the browser's own zone, so it is right before anybody visits
 * Settings, and it needs no round trip to be right. The cost is that it does
 * not follow the operator to another device, where it will again default
 * correctly.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'display-time-zone';

/** Fired when the preference changes, so open screens re-render. */
const CHANGED = 'display-time-zone-changed';

/** The browser's own zone, and the default when nothing has been chosen. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    // Every supported browser resolves this; 'UTC' keeps a rendering path
    // rather than letting a formatter throw inside a list of timestamps.
    return 'UTC';
  }
}

function isValid(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The chosen zone, or the browser's.
 *
 * Wrapped because `localStorage` throws outright in a private window and on a
 * page with site data blocked — and this is called by every timestamp on the
 * screen, so a throw here would blank a list rather than misformat one row.
 * A stored value that is no longer a zone the browser knows (a tzdata rename,
 * a hand-edited key) falls back rather than throwing for the same reason.
 */
export function displayTimeZone(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isValid(stored)) return stored;
  } catch {
    /* no storage; the browser's zone is still the right answer */
  }
  return browserTimeZone();
}

/** Writes the preference and tells every open screen. */
export function setDisplayTimeZone(zone: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, zone);
  } catch {
    /* The choice still applies to this page via the event below. */
  }
  window.dispatchEvent(new CustomEvent(CHANGED));
}

/** Forgets the choice, returning to whatever the browser says. */
export function clearDisplayTimeZone(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing stored to remove */
  }
  window.dispatchEvent(new CustomEvent(CHANGED));
}

/**
 * The zone, as a value a component re-renders on.
 *
 * `useSyncExternalStore` rather than `useState` plus an effect: the preference
 * lives outside React (in `localStorage`, and in another tab), and this is the
 * hook built for reading exactly that kind of store without tearing.
 *
 * Only screens that must update WITHOUT a navigation need it — the Settings
 * screen, which shows a live example of the format beside the control. Every
 * other screen calls `formatTimestamp` directly and picks the new zone up on
 * its next render, which is what changing a display preference and going to
 * look at something already does.
 */
export function useDisplayTimeZone(): string {
  return useSyncExternalStore(onTimeZoneChange, displayTimeZone, browserTimeZone);
}

export function onTimeZoneChange(listener: () => void): () => void {
  window.addEventListener(CHANGED, listener);
  // `storage` fires for OTHER tabs, so a second window follows along.
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(CHANGED, listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * An instant from either form the API sends, or null when it is neither.
 *
 * NOT `new Date(value)` ALONE, and `lib/history.ts` already said why: a
 * timestamptz read straight out of PostgreSQL arrives as
 * "2026-09-13 14:02:11.123+00" — a space instead of the T, and a two-digit
 * offset — which is not ISO 8601, and how a browser parses a non-ISO string is
 * explicitly implementation-defined. Some return a date in LOCAL time, which is
 * the worst possible outcome for a function whose whole job is to control which
 * zone a moment is shown in: the value would be silently shifted twice.
 *
 * So the two known shapes are normalised to ISO first, and anything else
 * returns null so the caller can print the raw string rather than a confident
 * wrong time.
 */
function parseInstant(value: string): Date | null {
  const text = value.trim();
  const match =
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/.exec(
      text,
    );
  if (!match) return null;

  const [, date, time, rawZone] = match;
  /*
   * A missing offset is read as UTC. Every timestamp this app displays comes
   * from a `timestamptz` column, so the server has already resolved it to an
   * instant; treating a bare one as local would reintroduce the shift above.
   */
  let zone = rawZone ?? 'Z';
  if (/^[+-]\d{2}$/.test(zone)) zone = `${zone}:00`;
  else if (/^[+-]\d{4}$/.test(zone)) zone = `${zone.slice(0, 3)}:${zone.slice(3)}`;

  const at = new Date(`${date}T${time.length === 5 ? `${time}:00` : time}${zone}`);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * An instant, in the chosen zone, with the zone named.
 *
 * THE ABBREVIATION IS NOT DECORATION. The whole point of the setting is that
 * the reader may not be in the zone they are reading; a timestamp that has been
 * shifted and does not say so is worse than one that was never shifted, because
 * it looks like the old behaviour. `timeZoneName: 'short'` renders CDT, GMT+2,
 * and so on.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = parseInstant(iso);
  if (at === null) return String(iso);
  try {
    /*
     * COMPONENT OPTIONS, NOT `dateStyle`/`timeStyle`.
     *
     * ECMA-402 forbids combining those two with individual components, and
     * `timeZoneName` is one — so `{ dateStyle, timeStyle, timeZoneName }`
     * throws a TypeError. The first version did exactly that, and because the
     * call is wrapped it did not crash: every timestamp in the app quietly fell
     * through to the ISO string in the catch below, which looks like a
     * formatting choice rather than a failure. Caught by the tests in
     * `test/history.test.ts`, which is the only reason it is not in production.
     */
    return new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimeZone(),
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(at);
  } catch {
    return at.toISOString();
  }
}

/**
 * The zones offered in the selector.
 *
 * `Intl.supportedValuesOf('timeZone')` is the browser's own list — every zone
 * it will accept, which is exactly the set that cannot fail validation. Where
 * it is missing (older Safari) a short list covers the US business zones plus
 * UTC, and the current value is unioned in so a zone already chosen never
 * disappears from the control that set it.
 */
export function availableTimeZones(): string[] {
  let zones: string[];
  try {
    zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf?.('timeZone') ?? [];
  } catch {
    zones = [];
  }
  if (zones.length === 0) {
    zones = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'Pacific/Honolulu',
    ];
  }
  /*
   * UTC IS ADDED, because the browser's list does not contain it.
   *
   * `Intl.supportedValuesOf('timeZone')` returns 418 canonical IANA zones and
   * neither "UTC" nor "Etc/UTC" is among them — they are links, not canonical
   * names, and the method deliberately omits links. Every formatter ACCEPTS
   * "UTC", so it works perfectly once chosen; it simply could not be chosen.
   *
   * It belongs at the top of an accounting tool's list. The server stores every
   * instant in UTC and `scripts/q.mjs` prints them that way, so "show me what
   * the database sees" is a real thing to want when reconciling a timestamp
   * against a log.
   */
  const current = displayTimeZone();
  const withUtc = zones.includes('UTC') ? zones : ['UTC', ...zones];
  return withUtc.includes(current) ? withUtc : [current, ...withUtc];
}
